import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";

const DataContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const DataProvider = ({ children }) => {
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineCategories, setPipelineCategories] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [automations, setAutomations] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Contacts
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    }
    setLoading(false);
  }, []);

  const createContact = async (data) => {
    const response = await axios.post(`${API_URL}/contacts`, data);
    setContacts(prev => [response.data, ...prev]);
    return response.data;
  };

  const updateContact = async (id, data) => {
    const response = await axios.put(`${API_URL}/contacts/${id}`, data);
    setContacts(prev => prev.map(c => c.id === id ? response.data : c));
    return response.data;
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API_URL}/contacts/${id}`);
    setContacts(prev => prev.filter(c => c.id !== id));
  };

  // Projects
  const fetchProjects = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_URL}/projects?${params}`);
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
    setLoading(false);
  }, []);

  const createProject = async (data) => {
    const response = await axios.post(`${API_URL}/projects`, data);
    setProjects(prev => [response.data, ...prev]);
    return response.data;
  };

  const updateProject = async (id, data) => {
    const response = await axios.put(`${API_URL}/projects/${id}`, data);
    setProjects(prev => prev.map(p => p.id === id ? response.data : p));
    return response.data;
  };

  const updateProjectStage = async (id, stage) => {
    const response = await axios.patch(`${API_URL}/projects/${id}/stage?stage=${stage}`);
    setProjects(prev => prev.map(p => p.id === id ? response.data : p));
    return response.data;
  };

  const deleteProject = async (id) => {
    await axios.delete(`${API_URL}/projects/${id}`);
    setProjects(prev => prev.filter(p => p.id !== id));
  };

  // Suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  }, []);

  const createSupplier = async (data) => {
    const response = await axios.post(`${API_URL}/suppliers`, data);
    setSuppliers(prev => [...prev, response.data]);
    return response.data;
  };

  const updateSupplier = async (id, data) => {
    const response = await axios.put(`${API_URL}/suppliers/${id}`, data);
    setSuppliers(prev => prev.map(s => s.id === id ? response.data : s));
    return response.data;
  };

  const deleteSupplier = async (id) => {
    await axios.delete(`${API_URL}/suppliers/${id}`);
    setSuppliers(prev => prev.filter(s => s.id !== id));
  };

  // Tasks
  const fetchTasks = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_URL}/tasks?${params}`);
      setTasks(response.data);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  }, []);

  const createTask = async (data) => {
    const response = await axios.post(`${API_URL}/tasks`, data);
    setTasks(prev => [...prev, response.data]);
    return response.data;
  };

  const updateTask = async (id, data) => {
    const response = await axios.put(`${API_URL}/tasks/${id}`, data);
    setTasks(prev => prev.map(t => t.id === id ? response.data : t));
    return response.data;
  };

  const updateTaskStatus = async (id, status) => {
    const response = await axios.patch(`${API_URL}/tasks/${id}/status?status=${status}`);
    setTasks(prev => prev.map(t => t.id === id ? response.data : t));
    return response.data;
  };

  const deleteTask = async (id) => {
    await axios.delete(`${API_URL}/tasks/${id}`);
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  // Pipeline Stages
  const fetchPipelineStages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pipeline-stages`);
      setPipelineStages(response.data);
    } catch (error) {
      console.error("Failed to fetch pipeline stages:", error);
    }
  }, []);

  const fetchPipelineCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pipeline-categories`);
      setPipelineCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch pipeline categories:", error);
    }
  }, []);

  const createPipelineCategory = async (data) => {
    const response = await axios.post(`${API_URL}/pipeline-categories`, data);
    setPipelineCategories(prev => [...prev, response.data].sort((a, b) => a.order - b.order));
    return response.data;
  };

  const updatePipelineCategory = async (id, data) => {
    const response = await axios.put(`${API_URL}/pipeline-categories/${id}`, data);
    setPipelineCategories(prev => prev.map(c => c.id === id ? response.data : c).sort((a, b) => a.order - b.order));
    return response.data;
  };

  const deletePipelineCategory = async (id) => {
    await axios.delete(`${API_URL}/pipeline-categories/${id}`);
    setPipelineCategories(prev => prev.filter(c => c.id !== id));
    await fetchPipelineStages();
  };

  const createPipelineStage = async (data) => {
    const response = await axios.post(`${API_URL}/pipeline-stages`, data);
    setPipelineStages(prev => [...prev, response.data].sort((a, b) => a.order - b.order));
    return response.data;
  };

  const updatePipelineStage = async (id, data) => {
    const response = await axios.put(`${API_URL}/pipeline-stages/${id}`, data);
    setPipelineStages(prev => prev.map(s => s.id === id ? response.data : s).sort((a, b) => a.order - b.order));
    return response.data;
  };

  const deletePipelineStage = async (id) => {
    await axios.delete(`${API_URL}/pipeline-stages/${id}`);
    setPipelineStages(prev => prev.filter(s => s.id !== id));
  };

  // Dropdown Options
  const fetchDropdownOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdown-options`);
      setDropdownOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch dropdown options:", error);
    }
  }, []);

  const createDropdownOption = async (data) => {
    const response = await axios.post(`${API_URL}/dropdown-options`, data);
    setDropdownOptions(prev => {
      const category = data.category;
      return {
        ...prev,
        [category]: [...(prev[category] || []), response.data]
      };
    });
    return response.data;
  };

  const updateDropdownOption = async (id, data) => {
    const response = await axios.put(`${API_URL}/dropdown-options/${id}`, data);
    setDropdownOptions(prev => {
      const category = data.category;
      return {
        ...prev,
        [category]: (prev[category] || []).map(o => o.id === id ? response.data : o)
      };
    });
    return response.data;
  };

  const deleteDropdownOption = async (id, category) => {
    await axios.delete(`${API_URL}/dropdown-options/${id}`);
    setDropdownOptions(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter(o => o.id !== id)
    }));
  };

  // Automations
  const fetchAutomations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/automations`);
      setAutomations(response.data);
    } catch (error) {
      console.error("Failed to fetch automations:", error);
    }
  }, []);

  const createAutomation = async (data) => {
    const response = await axios.post(`${API_URL}/automations`, data);
    setAutomations(prev => [...prev, response.data]);
    return response.data;
  };

  const updateAutomation = async (id, data) => {
    const response = await axios.put(`${API_URL}/automations/${id}`, data);
    setAutomations(prev => prev.map(a => a.id === id ? response.data : a));
    return response.data;
  };

  const deleteAutomation = async (id) => {
    await axios.delete(`${API_URL}/automations/${id}`);
    setAutomations(prev => prev.filter(a => a.id !== id));
  };

  // Dashboard
  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    }
  }, []);

  // Activities
  const fetchActivities = useCallback(async (entityType, entityId) => {
    try {
      const params = new URLSearchParams({ entity_type: entityType, entity_id: entityId }).toString();
      const response = await axios.get(`${API_URL}/activities?${params}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      return [];
    }
  }, []);

  // Quote Builder
  const [quotes, setQuotes] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [quoteSettings, setQuoteSettings] = useState(null);

  const fetchQuotes = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_URL}/quotes?${params}`);
      setQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch quotes:", error);
    }
  }, []);

  const createQuote = async (data) => {
    const response = await axios.post(`${API_URL}/quotes`, data);
    setQuotes(prev => [response.data, ...prev]);
    return response.data;
  };

  const updateQuote = async (id, data) => {
    const response = await axios.put(`${API_URL}/quotes/${id}`, data);
    setQuotes(prev => prev.map(q => q.id === id ? response.data : q));
    return response.data;
  };

  const updateQuoteStatus = async (id, status) => {
    const response = await axios.patch(`${API_URL}/quotes/${id}/status?status=${status}`);
    setQuotes(prev => prev.map(q => q.id === id ? response.data : q));
    return response.data;
  };

  const signQuote = async (id, signatureType, signatureData) => {
    const response = await axios.post(`${API_URL}/quotes/${id}/sign?signature_type=${signatureType}`, signatureData);
    setQuotes(prev => prev.map(q => q.id === id ? response.data : q));
    return response.data;
  };

  const calculateQuote = async (id) => {
    const response = await axios.post(`${API_URL}/quotes/${id}/calculate`);
    setQuotes(prev => prev.map(q => q.id === id ? response.data : q));
    return response.data;
  };

  const deleteQuote = async (id) => {
    await axios.delete(`${API_URL}/quotes/${id}`);
    setQuotes(prev => prev.filter(q => q.id !== id));
  };

  const fetchQuoteTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/quote-templates`);
      setQuoteTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch quote templates:", error);
    }
  }, []);

  const createQuoteTemplate = async (data) => {
    const response = await axios.post(`${API_URL}/quote-templates`, data);
    setQuoteTemplates(prev => [...prev, response.data]);
    return response.data;
  };

  const updateQuoteTemplate = async (id, data) => {
    const response = await axios.put(`${API_URL}/quote-templates/${id}`, data);
    setQuoteTemplates(prev => prev.map(t => t.id === id ? response.data : t));
    return response.data;
  };

  const deleteQuoteTemplate = async (id) => {
    await axios.delete(`${API_URL}/quote-templates/${id}`);
    setQuoteTemplates(prev => prev.filter(t => t.id !== id));
  };

  const fetchPriceList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/price-list`);
      setPriceList(response.data);
    } catch (error) {
      console.error("Failed to fetch price list:", error);
    }
  }, []);

  const createPriceListItem = async (data) => {
    const response = await axios.post(`${API_URL}/price-list`, data);
    setPriceList(prev => [...prev, response.data]);
    return response.data;
  };

  const updatePriceListItem = async (id, data) => {
    const response = await axios.put(`${API_URL}/price-list/${id}`, data);
    setPriceList(prev => prev.map(p => p.id === id ? response.data : p));
    return response.data;
  };

  const deletePriceListItem = async (id) => {
    await axios.delete(`${API_URL}/price-list/${id}`);
    setPriceList(prev => prev.filter(p => p.id !== id));
  };

  const fetchQuoteSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/quote-settings`);
      setQuoteSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch quote settings:", error);
    }
  }, []);

  const updateQuoteSettings = async (data) => {
    const response = await axios.put(`${API_URL}/quote-settings`, data);
    setQuoteSettings(response.data);
    return response.data;
  };

  return (
    <DataContext.Provider value={{
      // State
      contacts,
      projects,
      suppliers,
      tasks,
      pipelineStages,
      pipelineCategories,
      dropdownOptions,
      automations,
      dashboardStats,
      loading,
      
      // Contacts
      fetchContacts,
      createContact,
      updateContact,
      deleteContact,
      
      // Projects
      fetchProjects,
      createProject,
      updateProject,
      updateProjectStage,
      deleteProject,
      
      // Suppliers
      fetchSuppliers,
      createSupplier,
      updateSupplier,
      deleteSupplier,
      
      // Tasks
      fetchTasks,
      createTask,
      updateTask,
      updateTaskStatus,
      deleteTask,
      
      // Pipeline Stages
      fetchPipelineStages,
      fetchPipelineCategories,
      createPipelineCategory,
      updatePipelineCategory,
      deletePipelineCategory,
      createPipelineStage,
      updatePipelineStage,
      deletePipelineStage,
      
      // Dropdown Options
      fetchDropdownOptions,
      createDropdownOption,
      updateDropdownOption,
      deleteDropdownOption,
      
      // Automations
      fetchAutomations,
      createAutomation,
      updateAutomation,
      deleteAutomation,
      
      // Dashboard
      fetchDashboardStats,
      
      // Activities
      fetchActivities,
      
      // Quotes
      quotes,
      fetchQuotes,
      createQuote,
      updateQuote,
      updateQuoteStatus,
      signQuote,
      calculateQuote,
      deleteQuote,
      
      // Quote Templates
      quoteTemplates,
      fetchQuoteTemplates,
      createQuoteTemplate,
      updateQuoteTemplate,
      deleteQuoteTemplate,
      
      // Price List
      priceList,
      fetchPriceList,
      createPriceListItem,
      updatePriceListItem,
      deletePriceListItem,
      
      // Quote Settings
      quoteSettings,
      fetchQuoteSettings,
      updateQuoteSettings
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within DataProvider");
  }
  return context;
};
